import {
    Component,
    OnDestroy,
    OnInit,
    ElementRef,
    Renderer2,
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    ViewEncapsulation,
    HostListener,
    Input,
    OnChanges,
    SimpleChanges,
} from '@angular/core';
import { NotificationData, SocketService } from '../../services/socket.service';
import { fmtDate, VbSnackbarService } from '@viewbid/ui';
import {
    AuctionCatalogsService,
    GetMessageByIdResponse,
    GetMessageResponse,
    NotificationsService,
} from '@viewbid/ts-angular-client';
import { ToggleService } from 'src/app/viewbid/modules/auctions/services/toggle.service';
import { Subscription, firstValueFrom } from 'rxjs';
import {
    NotificationService,
    NotificationMessage,
} from '../../services/notification.service';
import { TranslateService } from '@ngx-translate/core';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { Store } from '@ngrx/store';
import { selectUser } from 'src/app/viewbid/state/app-selectors';
import { AppState } from 'src/app/viewbid/state/app-state';
import { User } from '@auth0/auth0-angular';
import { ActivatedRoute, Router } from '@angular/router';
import { ProductService } from '@/shared/services/product.service';
import { BiddingUtils } from '@/vb-utils/bidding.util';

interface OutbidEventData {
    lotTitle: string;
    lot?: { lotNumber?: string; auctionName?: string };
}

@Component({
    selector: 'app-notifications',
    templateUrl: './notifications.component.html',
    styleUrls: ['./notifications.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    encapsulation: ViewEncapsulation.None,
})
export class NotificationsComponent implements OnInit, OnDestroy, OnChanges {
    private static readonly AUCTION_TITLE_MAX_LENGTH = 30;
    notificationMessages: NotificationMessage[] = [];
    // Is notification dropdown open
    isNotificationDropdownOpen: boolean = false;
    private readonly subscriptions: Subscription[] = [];
    private readonly user$ = this.store.select<User | null>(selectUser);
    public pageNo: number = 1;
    public pageSize: number = 10;
    public totalCount: number = 0;

    /** Accessible name for the bell button — carries the unread count, in
     *  the same phrasing as the library header's built-in bell
     *  (vb-app-header.notifAriaLabel), so the projected bell reads
     *  identically to assistive tech. */
    get bellAriaLabel(): string {
        if (this.totalCount <= 0) return 'Notifications';
        const count: string =
            this.totalCount > 99 ? '99+' : String(this.totalCount);
        return `Notifications, ${count} unread`;
    }
    public paginate: any = {};
    isLoadingMore = false;
    currentUserId: string = '';
    @Input() isMobile: boolean = false;

    constructor(
        private readonly store: Store<AppState>,
        private readonly socketService: SocketService,
        private readonly snackbarService: VbSnackbarService,
        private readonly auctionService: AuctionCatalogsService,
        public toggleService: ToggleService,
        private readonly notificationsApi: NotificationsService,
        private readonly notificationService: NotificationService,
        private readonly renderer: Renderer2,
        private readonly elRef: ElementRef,
        private readonly translateService: TranslateService,
        private readonly sanitizer: DomSanitizer,
        private readonly router: Router,
        private readonly route: ActivatedRoute,
        private readonly productService: ProductService,
        private readonly biddingUtils: BiddingUtils,
        private ref: ChangeDetectorRef,
    ) {
        // Listen to resource events from socket (for bid notifications)
        this.subscriptions.push(this.subscribeToSocketEvents());

        // Listen to notification messages updates
        this.subscriptions.push(
            this.notificationService.notificationMessages$.subscribe(
                (messages: NotificationMessage[]) => {
                    this.notificationMessages = messages;
                },
            ),
        );

        // Keep this dropdown in sync with the shared toggle state so opening
        // another toggle (e.g. the header profile menu) — which clears all
        // toggles — closes this one too. The template reads the toggle state
        // directly, so an OnPush re-check is needed; the local flag is kept in
        // step so the outside-click handler doesn't act on a stale value.
        this.subscriptions.push(
            this.toggleService.changes$.subscribe(
                (state: { [key: string]: boolean }) => {
                    this.isNotificationDropdownOpen =
                        state['isNotificationDropdownOpen'] ?? false;
                    this.ref.markForCheck();
                },
            ),
        );

        // Listen to socket notifications (for user messages, etc)
        this.subscriptions.push(
            this.socketService.notification$.subscribe(
                (notification: NotificationData) => {
                    if (notification.type === 'HAND_SHAKE_NOTIFY') {
                        return;
                    }
                    const notificationHtml: string = this.notificationTemplate(
                        notification,
                        notification.timestamp,
                    );
                    this.notificationService.addNotification({
                        message: notificationHtml,
                        url: notification?.moreDetails,
                    });
                    this.totalCount += 1;
                },
            ),
        );
    }
    ngOnChanges(changes: SimpleChanges): void {
        if (changes['isMobile'] && !changes['isMobile'].firstChange) {
            this.isMobile = changes['isMobile'].currentValue;
        }
    }

    /**
     * This ensures the message dropdown is dismissed when the user
     * clicks outside of it
     */
    @HostListener('document:click', ['$event'])
    onDocumentClick(_event: Event): void {
        const targetElement: HTMLElement = _event.target as HTMLElement;
        const clickedInside: boolean =
            this.elRef.nativeElement.contains(targetElement);
        if (!clickedInside && this.isNotificationDropdownOpen) {
            this.openNotificationDropdown(_event);
        }
    }

    navToNotification(_event: Event, url: any): void {
        if (url) {
            const isUrlAdmin: boolean = this.isUrlAdmin(url);
            if (isUrlAdmin) {
                window.open(url);
            } else {
                const parsed: URL | null = this.isUrlAbsolute(url)
                    ? new URL(url)
                    : null;
                const path: string = parsed
                    ? `${parsed.pathname}${parsed.search}${parsed.hash}`
                    : url;
                // navigateByUrl, NOT navigate([path]): notification links are
                // raw URL strings that can carry matrix params
                // (/invoice-actions;id=…). navigate() treats the whole string
                // as ONE segment and percent-encodes its semicolons (%3B),
                // which then matches no route and 404s — navigateByUrl parses
                // the string the way the address bar would.
                this.router.navigateByUrl(path);
            }
        }
    }

    private isUrlAdmin(url: string): boolean {
        return /\/admin\/.*/.test(url);
    }

    private isUrlAbsolute(url: string): boolean {
        return /^(?:[a-z+]+:)?\/\//.test(url);
    }

    private getIcon(type: string): string {
        if (!type) {
            return 'icon_auction.svg';
        }
        if (
            type.includes('Welcome') ||
            type.includes('A new user has registered')
        ) {
            return 'emoji-smile.svg';
        }
        switch (type.trim()) {
            case 'Your Account Was Approved':
                return 'circle-check-regular.svg';
            case "You're proxy bid has been exceeded":
                return 'icon_auction.svg';
            case 'Auction Open':
                return 'icon_auction.svg';
            case "You've been outbid!":
                return 'hand-thumbs-down.svg';
            case 'You won your auction!':
                return 'trophy.svg';
            case 'Auction Reminder!':
                return 'exclamation-circle.svg';
            case 'Lot Closed!':
                return 'icon_auction.svg';
            case 'Lot Update':
                return 'exclamation-diamond.svg';
            case 'Lot item closing soon!':
                return 'icon_auction.svg';
            case 'Price Alert!':
                return 'circle-check-regular.svg';
            case 'Shipping Request':
                return 'icon_pop.svg';
            case 'Invoice Status Updated':
                return 'circle-check-regular.svg';
            case 'Shipping Quote':
                return 'icon_pop.svg';
            case 'New Message':
                return 'icon_envelope.svg';
            default:
                return 'icon_auction.svg';
        }
    }

    private notificationTemplate(notification: any, date: any): string {
        this.ref.markForCheck();
        const validDate: string =
            date && !isNaN(Date.parse(date)) ? date : new Date().toISOString();
        // Row layout mirrors the search panel's result rows (icon tile ·
        // stacked meta · mono date); classes are styled in this component's
        // SCSS (encapsulation: None). The icon is decorative — the title text
        // sits right beside it — so alt stays empty.
        return `<div class="notification-row">
                    <img class="notification-row__icon" src="/assets/img/icon/${this.getIcon(notification?.title)}" alt=""/>
                    <span class="notification-row__meta">
                        <span class="notification-row__title">${notification?.title ?? ''}</span>
                        <span class="notification-row__desc">${notification?.description ?? ''}</span>
                    </span>
                    <span class="notification-row__date">${fmtDate(validDate)}</span>
                </div>`;
    }

    private parseMessage(input: string | undefined): any {
        if (!input) {
            return {};
        }

        const obj: any = {};

        // Remove outer braces and newlines
        const message: string = input.replace(/^{|}$/g, '').replace(/\n/g, '');

        // Match all "key": "value" or "key": value (value may be non-quoted like booleans or numbers)
        const regex: RegExp = /"([^"]+)"\s*:\s*(?:"([^"]*)"|([^",{}]+))/g;

        let match: RegExpExecArray | null;
        while ((match = regex.exec(message)) !== null) {
            const key: string = match[1].trim();
            const value: string = (match[2] ?? match[3] ?? '').trim();
            if (this.isKeyUrl(key) && value) {
                obj['url'] = value;
            }
            obj[key] = value;
        }
        return obj;
    }

    private isKeyUrl(key: string): boolean {
        return key === 'moreDetails' || key === 'url';
    }
    private fetchNotificationMessages(): void {
        this.notificationsApi
            .getMessages(this.pageNo, this.pageSize, 'null')
            .subscribe({
                next: (res: GetMessageResponse | null) => {
                    this.isLoadingMore = false;
                    if (res && res.data && res.data.length) {
                        this.totalCount = res.totalCount ?? 0;
                        this.pageNo = res.page ?? 0;
                        this.paginate = this.productService.getPager(
                            res.totalCount ?? 0,
                            Number(+(res.page ?? 0)),
                            res.pageSize,
                            false,
                        );
                        const newNotifications: NotificationMessage[] = [];
                        res.data.forEach(
                            (notificationMessage: GetMessageByIdResponse) => {
                                try {
                                    const notification: any = this.parseMessage(
                                        notificationMessage?.message,
                                    );
                                    newNotifications.push({
                                        message: this.notificationTemplate(
                                            notification,
                                            notificationMessage?.decayDate,
                                        ),
                                        title: notification?.title,
                                        url: notification.url,
                                    });
                                } catch (error) {
                                    this.snackbarService.error(
                                        this.translateService.instant(
                                            'Notifications.notificationsParseError',
                                        ),
                                    );
                                }
                            },
                        );
                        this.notificationService.appendNotifications(
                            newNotifications,
                        );
                    } else {
                        this.totalCount = 0;
                        this.pageNo = 1;
                        this.paginate = this.productService.getPager(
                            0,
                            0,
                            res?.pageSize,
                            false,
                        );
                        this.paginate.currentPage = 0;
                        this.isLoadingMore = false;
                    }
                },
                error: () => {
                    this.snackbarService.error(
                        this.translateService.instant(
                            'Notifications.notificationsFetchError',
                        ),
                    );
                },
            });
    }

    private subscribeToSocketEvents(): Subscription {
        return this.socketService.resource$.subscribe((data: string) => {
            const eventResponse: any = JSON.parse(data);
            if (eventResponse.action === 'NEW_HIGH_BID') {
                this.handleNewHighBidEvent(eventResponse.data);
            }
        });
    }

    private truncateAuctionTitle(auctionTitle: string): string {
        return auctionTitle.length >
            NotificationsComponent.AUCTION_TITLE_MAX_LENGTH
            ? `${auctionTitle.slice(0, NotificationsComponent.AUCTION_TITLE_MAX_LENGTH)}...`
            : auctionTitle;
    }

    private handleNewHighBidEvent(data: OutbidEventData): void {
        const lotNumber: string = data.lot?.lotNumber || data.lotTitle;
        const auctionTitle: string = this.truncateAuctionTitle(
            data.lot?.auctionName ?? '',
        );
        const message: string = auctionTitle
            ? this.translateService.instant('Bidding.outbidMessage', {
                  lotNumber,
                  auctionTitle,
              })
            : this.translateService.instant(
                  'Bidding.outbidMessageWithoutAuction',
                  { lotNumber },
              );
        // Toast only. An outbid reaches this client twice: this direct
        // NEW_HIGH_BID event, and the notifications pipeline's 'notification'
        // push (which also writes the DB row the REST count reflects). The
        // push owns the bell count and dropdown entry — adding them here too
        // double-counted every outbid.
        this.snackbarService.warning(message);
    }

    async ngOnInit(): Promise<void> {
        this.pageNo = this.route.snapshot.queryParams['page'] || 1;
        this.biddingUtils
            .getCurrentUserId()
            .subscribe((currentUserId: string) => {
                this.currentUserId = currentUserId;
            });
        this.renderer.listen('window', 'click', (e: Event) => {
            // Close only this dropdown on an outside click. Clearing every
            // toggle here (setAllToggleStateToFalse) would also close the header
            // profile menu on the very click that opens it — this listener fires
            // right after that button's handler — so the menu could never open.
            // The profile menu manages its own outside-click close.
            if (
                !this.elRef.nativeElement.contains(e.target) &&
                this.toggleService.getToggleState('isNotificationDropdownOpen')
            ) {
                this.toggleService.setToggleState(
                    'isNotificationDropdownOpen',
                    false,
                );
            }
        });
        if (this.currentUserId) {
            this.setPage(this.pageNo);
            // The list lives in a root-singleton service that outlives this
            // component — the header unmounts on layout-less routes (e.g.
            // /404) and remounts on return, re-running this init. Start the
            // initial load from a clean list so a remount REBUILDS it instead
            // of appending a duplicate first page (append is only right for
            // the infinite-scroll pages that follow).
            this.notificationService.clearNotifications();
            this.fetchNotificationMessages();
        }
    }

    setPage(page: number): void {
        this.pageNo = page;
    }

    /** Explicit close for the dropdown's X. Outside-click dismissal still
     *  works, but on mobile that tap usually activates whatever sits under
     *  it — the X closes without side effects. */
    public closeNotificationDropdown(event: Event): void {
        event.stopPropagation();
        this.toggleService.setToggleState('isNotificationDropdownOpen', false);
        this.isNotificationDropdownOpen = false;
        this.ref.markForCheck();
    }

    // Open/close notification dropdown
    public openNotificationDropdown(event: Event): void {
        event.stopPropagation(); // Prevent the click event from propagating to the window
        // Toggle notification dropdown open state
        this.isNotificationDropdownOpen = this.toggleService.onToggleClicked(
            'isNotificationDropdownOpen',
            !this.toggleService.getToggleState('isNotificationDropdownOpen'),
        );
    }

    public async clearNotifications(): Promise<void> {
        const user: User | null = await firstValueFrom(this.user$);
        const accountMappingsId: string | undefined =
            user && 'accountMappingsId' in user
                ? user['accountMappingsId']
                : undefined;

        if (accountMappingsId) {
            this.notificationsApi.markAsViewed(accountMappingsId).subscribe({
                next: () => {
                    this.notificationService.clearNotifications();
                    this.totalCount = 0;
                    this.ref.markForCheck();
                },
                error: () =>
                    this.snackbarService.error(
                        this.translateService.instant(
                            'Notifications.clearError',
                        ),
                    ),
            });
        }
    }

    ngOnDestroy(): void {
        this.subscriptions.forEach((subscription: Subscription) =>
            subscription.unsubscribe(),
        );
    }

    sanitizeHtml(html: string): SafeHtml {
        return this.sanitizer.bypassSecurityTrustHtml(html);
    }

    onNotificationScroll(event: Event): void {
        const target: HTMLElement = event.target as HTMLElement;
        const threshold: number = 100;
        if (
            !this.isLoadingMore &&
            target.scrollTop + target.clientHeight >=
                target.scrollHeight - threshold &&
            this.paginate.currentPage < this.paginate.totalPages
        ) {
            this.isLoadingMore = true;
            this.setPage(this.pageNo + 1);
            this.fetchNotificationMessages();
        }
    }
}
